import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { toast } from 'react-toastify';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { SportItemCard } from './sportItemCard/sportItemCard';
import {
  useGetNextGamesInfoByLocationMutation,
  useGetSportUserPreferencesMutation,
  useSetNewExecutionPriorityMutation
} from '../../../../services/sportService/sportService';
import {
  INextGamesInfo,
  ISportUserPreferences
} from '../../../../types/sports/sportsInterfaces';
import './teamPreference.css';

export const SportUserPreferences = () => {
  const [sportUserEvents, setSportUserEvents] = useState<
    ISportUserPreferences[]
  >([]);
  const [nextGames, setNextGames] = useState<INextGamesInfo[]>([]);
  const [disableControls, setDisableControls] = useState(false);
  const { locationId } = useParams();
  const navigate = useNavigate();

  const [
    getSportUserPreferences,
    { data: sportUserPreferences, isSuccess: isSuccessGetData }
  ] = useGetSportUserPreferencesMutation();
  const [
    getNextGamesInfoByLocation,
    { data: nextGamesInfo, isSuccess: isSuccessGetNextGamesInfo }
  ] = useGetNextGamesInfoByLocationMutation();
  const [
    setNewExecutionPriority,
    { isSuccess: isSuccessSetNewExecutionPriority }
  ] = useSetNewExecutionPriorityMutation();

  const loadTable = async () => {
    getSportUserPreferences(Number(locationId));
  };

  const loadNextGamesInfo = async () => {
    getNextGamesInfoByLocation(Number(locationId));
  };

  const orderByPriority = (events: ISportUserPreferences[]) => {
    return [...events].sort((a, b) => a.executionOrder - b.executionOrder);
  };

  const updateExecutionPriority = async (
    eventsChanged: ISportUserPreferences[]
  ) => {
    const newOrderPayload = {
      locationId: Number(locationId),
      order: eventsChanged.map((item, index) => ({
        id: item.id,
        executionOrder: index + 1
      }))
    };

    try {
      await setNewExecutionPriority(newOrderPayload);
      loadTable();
    } catch (error) {
      toast.error('Error updating execution priority');
    }
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedEvents = Array.from(sportUserEvents);
    const [movedEvent] = reorderedEvents.splice(result.source.index, 1);
    reorderedEvents.splice(result.destination.index, 0, movedEvent);

    const updatedEvents = reorderedEvents.map((event, index) => ({
      ...event,
      executionOrder: index + 1
    }));

    setSportUserEvents(updatedEvents);
    updateExecutionPriority(updatedEvents);
  };

  const toggleControlBtns = (state: boolean) => {
    setDisableControls(state);
  };

  const add = () => {
    navigate(`/Locations/${locationId}/sport-preferences/new`);
  };

  const goToSelectTeams = (e: any) => {
    e.preventDefault();
    navigate(
      `/Locations/${locationId}/sport-preferences/select-teams-for-subscription`
    );
  };

  useEffect(() => {
    loadTable();
  }, []);

  useEffect(() => {
    if (isSuccessGetData && sportUserPreferences) {
      const sortedEvents = orderByPriority(sportUserPreferences.data || []);
      setSportUserEvents(sortedEvents);
      loadNextGamesInfo();
    }
  }, [isSuccessGetData, sportUserPreferences]);

  useEffect(() => {
    if (isSuccessGetNextGamesInfo && nextGamesInfo) {
      setNextGames(nextGamesInfo.data);
    }
  }, [isSuccessGetNextGamesInfo]);

  useEffect(() => {
    if (isSuccessSetNewExecutionPriority) {
      toast.success('Execution priority updated successfully');
      loadTable();
    }
  }, [isSuccessSetNewExecutionPriority]);

  return (
    <div className="catalog-container-with-scroll">
      <div className="row">
        <div className="col-12">
          <Card>
            <div className="card-body-with-scroll">
              {sportUserEvents.length > 0 && nextGames && (
                <div>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="sportUserEvents">
                      {(provided: any) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="row"
                        >
                          {sportUserEvents.map((teamInfo, index) => (
                            <Draggable
                              key={teamInfo.teamId}
                              draggableId={teamInfo.teamId.toString()}
                              index={index}
                            >
                              {(providedDraggable: any) => (
                                <div
                                  className="col-12"
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  {...providedDraggable.dragHandleProps}
                                >
                                  <SportItemCard
                                    preference={teamInfo}
                                    reloadTable={loadTable}
                                    locationId={Number(locationId)}
                                    nextGame={
                                      nextGames.find(
                                        (game: INextGamesInfo) =>
                                          game.id === teamInfo.apiTeamId
                                      ) || {
                                        date: '',
                                        id: 0,
                                        home: '',
                                        away: '',
                                        league: ''
                                      }
                                    }
                                    disableControls={disableControls}
                                    toggleControls={toggleControlBtns}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
